import { z } from "zod";
import { IsoTimestamp, MatchId, PlayerId, SessionId } from "./ids.js";

// ── OTP via own MessagingService (Supabase-auth-adjacent, KICKOFF §2.3) ──────
export const OtpRequest = z.object({
  msisdn: z.string().regex(/^\+?[1-9]\d{7,14}$/), // E.164; hashed before storage
  idempotency_key: z.string().uuid(),
});
export type OtpRequest = z.infer<typeof OtpRequest>;

export const OtpVerify = z.object({
  msisdn: z.string().regex(/^\+?[1-9]\d{7,14}$/),
  code: z.string().regex(/^\d{6}$/),
  /**
   * Idempotent-retry semantics (DRIFT-001, Loop 1): when a verify SUCCEEDS,
   * the service caches the exact 200 session body against this key for a
   * short replay window (120s). A byte-identical retry — same key, same
   * msisdn, same code — re-acks that same 200 (same session_id, same
   * supabase_jwt) and emits no duplicate `otp_verified`, so a client that
   * lost the 200 over flaky data recovers by retrying the same request.
   * Only successful verifies are cached: wrong-code retries stay 401, and a
   * reused key with a different msisdn/code falls through to live
   * verification. Clients mint a fresh UUID per verify ATTEMPT and reuse it
   * only for network-level retries of that same attempt.
   */
  idempotency_key: z.string().uuid(),
});
export type OtpVerify = z.infer<typeof OtpVerify>;

// Ratified (contract-change request #3): the device hash rides this header on
// auth calls; AuthSession.device_hash binds the session to it.
export const DEVICE_HASH_HEADER = "x-device-hash";

// API response/request shapes (contract-change requests #1–#2).
export const OtpRequestAck = z.object({
  accepted: z.literal(true),
  retry_after_s: z.number().int().min(0).optional(),
});
export type OtpRequestAck = z.infer<typeof OtpRequestAck>;

export const ApiError = z.object({
  error: z.string().min(1),
  detail: z.unknown().optional(), // zod issues, {attempts_remaining}, … — structured, not prose
  retry_after_s: z.number().int().min(0).optional(), // present on rate-limit refusals (429)
});
export type ApiError = z.infer<typeof ApiError>;

// SMS template registry — every outbound message is one of these, no ad-hoc sends.
export const SmsTemplateId = z.enum([
  "otp_code",
  "magic_link_match",
  "night_open",
  "prize_won",
  "vault_expiry_nudge",
]);
export type SmsTemplateId = z.infer<typeof SmsTemplateId>;

export const DlrStatus = z.enum(["submitted", "delivered", "failed", "expired", "rejected"]);
export type DlrStatus = z.infer<typeof DlrStatus>;

// Delivery-receipt webhook payload from the provider (normalised by the adapter).
export const DlrEvent = z.object({
  provider_message_id: z.string().min(1),
  msisdn_hash: z.string().min(1),
  template_id: SmsTemplateId,
  status: DlrStatus,
  at: IsoTimestamp,
  raw: z.unknown(),
});
export type DlrEvent = z.infer<typeof DlrEvent>;

// ── Sessions: 90-day sliding ─────────────────────────────────────────────────
export const AuthSession = z.object({
  session_id: SessionId,
  player_id: PlayerId,
  issued_at: IsoTimestamp,
  last_seen_at: IsoTimestamp, // slide anchor: expiry = last_seen_at + 90d
  expires_at: IsoTimestamp,
  device_hash: z.string().min(1),
  // D-009 (Option A): Supabase-verifiable HS256 JWT (sub=player_id,
  // role=authenticated) minted at verify/redeem when SUPABASE_JWT_SECRET is
  // configured — the client hands it to supabase-js for RLS reads + realtime.
  // Short-lived (24h) vs the 90d session; re-minted on re-auth (refresh
  // endpoint rides the session slide in Loop 1).
  supabase_jwt: z.string().optional(),
});
export type AuthSession = z.infer<typeof AuthSession>;

// ── Magic links: signed, single-use deep links `/m/{id}?t=…` ─────────────────
export const MagicLinkPurpose = z.enum(["match_join", "match_room", "auth_recover"]);
export type MagicLinkPurpose = z.infer<typeof MagicLinkPurpose>;

export const IssueMagicLinkRequest = z.object({
  purpose: MagicLinkPurpose,
  match_id: MatchId.nullable(),
  player_id: PlayerId.nullable(), // null: link may mint a new player (first night)
  ttl_s: z.number().int().min(60).max(86400).optional(),
});
export type IssueMagicLinkRequest = z.infer<typeof IssueMagicLinkRequest>;

export const RedeemMagicLinkRequest = z.object({
  token: z.string().min(1),
});
export type RedeemMagicLinkRequest = z.infer<typeof RedeemMagicLinkRequest>;

export const MagicLinkClaims = z.object({
  purpose: MagicLinkPurpose,
  match_id: MatchId.nullable(),
  player_id: PlayerId.nullable(), // null when the link may mint a new player (first night)
  nonce: z.string().min(16), // single-use: burned server-side on redemption
  issued_at: IsoTimestamp,
  expires_at: IsoTimestamp,
});
export type MagicLinkClaims = z.infer<typeof MagicLinkClaims>;
